import { Component, inject, OnInit, OnDestroy, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import cloudflareConfig from '../../core/config/cloudflare.config';
import { COUNTRIES, Country, validatePhoneNumber, getPhoneValidationMessage, formatPhoneNumber } from '../../core/config/countries.config';
import { MetaService } from '../../core/services/meta.service';

declare global {
  interface Window {
    turnstile: any;
  }
}

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, Header, Footer],
  templateUrl: './register.html',
  styleUrls: ['./register.css']
})
export class RegisterComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private metaService = inject(MetaService);
  private isBrowser: boolean;

  // Wizard steps
  currentStep = 1; // 1: Email, 2: Complete data
  
  // Forms
  emailForm!: FormGroup;
  registerForm!: FormGroup;
  
  // State
  isLoading = false;
  errorMessage = '';
  registrationSuccess = false;
  registeredEmail = '';
  registrationMessage = '';
  
  // Email validation
  emailChecking = false;
  emailAvailable = false;
  
  // Cloudflare Turnstile
  turnstileToken: string | null = null;
  turnstileWidgetId: string | null = null;
  turnstileSiteKey: string = cloudflareConfig.turnstile.Site_Key;
  turnstileLoaded = false;

  // Password strength
  passwordStrength: 'weak' | 'medium' | 'strong' = 'weak';
  showPassword = false;
  showConfirmPassword = false;

  // Country selector for phone
  countries: Country[] = COUNTRIES;
  selectedCountry: string = 'EC'; // Ecuador por defecto
  showCountryDropdown = false;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.metaService.resetToDefault();

    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/profile']);
      return;
    }

    // Inicializar formularios
    this.initializeForms();

    // Renderizar Cloudflare Turnstile
    if (this.isBrowser) {
      this.waitForTurnstile();
      
      // Agregar listener para cerrar dropdown al hacer clic fuera
      document.addEventListener('click', this.closeDropdownOnClickOutside.bind(this));
    }
  }

  ngOnDestroy(): void {
    // Limpiar el widget de Turnstile
    if (this.isBrowser && this.turnstileWidgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (error) {
        console.error('Error al limpiar Turnstile:', error);
      }
    }
    
    // Remover listener de clicks
    if (this.isBrowser) {
      document.removeEventListener('click', this.closeDropdownOnClickOutside.bind(this));
    }
  }

  private closeDropdownOnClickOutside(): void {
    this.showCountryDropdown = false;
  }

  private initializeForms(): void {
    // Formulario de email (paso 1)
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    // Formulario de registro completo (paso 2)
    this.registerForm = this.fb.group({
      firstname: ['', [Validators.required, Validators.minLength(2)]],
      lastname: ['', [Validators.required, Validators.minLength(2)]],
      phone: ['', [Validators.required, this.phoneValidator.bind(this)]],
      password: ['', [Validators.required, Validators.minLength(8), this.mediumPasswordValidator]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });

    // Escuchar cambios en el password para actualizar la fuerza
    this.registerForm.get('password')?.valueChanges.subscribe(password => {
      this.updatePasswordStrength(password);
    });
  }

  // Validador de teléfono basado en país seleccionado
  private phoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const cleanPhone = value.replace(/\D/g, '');
    if (!cleanPhone) return null;

    if (!validatePhoneNumber(cleanPhone, this.selectedCountry)) {
      return { invalidPhone: true };
    }

    return null;
  }

  // Validador de contraseña nivel medio
  private mediumPasswordValidator = (control: AbstractControl): ValidationErrors | null => {
    const password = String(control.value || '');
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const minLength = password.length >= 8;

    const passwordValid = hasUpperCase && hasLowerCase && hasNumber && minLength;

    return !passwordValid ? { mediumPassword: true } : null;
  };

  // Validador de coincidencia de contraseñas
  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  // Actualizar indicador de fuerza de contraseña
  private updatePasswordStrength(password: string): void {
    const safePassword = String(password || '');
    if (!safePassword) {
      this.passwordStrength = 'weak';
      return;
    }

    const hasUpperCase = /[A-Z]/.test(safePassword);
    const hasLowerCase = /[a-z]/.test(safePassword);
    const hasNumber = /\d/.test(safePassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(safePassword);
    const minLength = safePassword.length >= 8;

    const checks = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, minLength].filter(Boolean).length;

    if (checks <= 2) {
      this.passwordStrength = 'weak';
    } else if (checks <= 4) {
      this.passwordStrength = 'medium';
    } else {
      this.passwordStrength = 'strong';
    }
  }

  // Cloudflare Turnstile
  private renderTurnstileAttempts = 0;
  private readonly MAX_RENDER_ATTEMPTS = 3;

  private waitForTurnstile(): void {
    if (!this.isBrowser) return;

    if (window.turnstile) {
      this.renderTurnstile();
      return;
    }

    let attempts = 0;
    const maxAttempts = 50;
    const checkInterval = setInterval(() => {
      attempts++;
      if (window.turnstile) {
        clearInterval(checkInterval);
        this.renderTurnstile();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('Timeout esperando Cloudflare Turnstile');
        this.errorMessage = 'Error al cargar el sistema de verificación';
      }
    }, 100);
  }

  private renderTurnstile(): void {
    if (!this.isBrowser || !window.turnstile) return;

    // Esperar a que el DOM se actualice completamente
    setTimeout(() => {
      const containerId = this.currentStep === 1 ? 'turnstile-container-step1' : 'turnstile-container-step2';
      const container = document.getElementById(containerId);
      
      if (!container) {
        console.error(`Contenedor de Turnstile no encontrado: ${containerId} (Intento ${this.renderTurnstileAttempts + 1}/${this.MAX_RENDER_ATTEMPTS})`);
        console.log('Current step:', this.currentStep);
        console.log('Step1 container:', document.getElementById('turnstile-container-step1'));
        console.log('Step2 container:', document.getElementById('turnstile-container-step2'));
        
        // Reintentar hasta el máximo de intentos
        if (this.renderTurnstileAttempts < this.MAX_RENDER_ATTEMPTS) {
          this.renderTurnstileAttempts++;
          setTimeout(() => {
            this.renderTurnstile();
          }, 500);
        } else {
          console.error('❌ No se pudo renderizar Turnstile después de múltiples intentos');
          this.errorMessage = 'Error al cargar el sistema de verificación. Por favor, recarga la página.';
          this.renderTurnstileAttempts = 0; // Reset para siguiente intento
        }
        return;
      }

      try {
        // Resetear contador de intentos al encontrar el contenedor
        this.renderTurnstileAttempts = 0;
        
        this.turnstileWidgetId = window.turnstile.render(`#${containerId}`, {
          sitekey: this.turnstileSiteKey,
          theme: 'light',
          callback: (token: string) => {
            this.turnstileToken = token;
            this.turnstileLoaded = true;
            this.cdr.detectChanges();
          },
          'error-callback': () => {
            console.error('Error en Turnstile');
            this.errorMessage = 'Error en la verificación de seguridad';
            this.turnstileLoaded = false;
            this.cdr.detectChanges();
          },
          'expired-callback': () => {
            console.warn('Token de Turnstile expirado');
            this.turnstileToken = null;
            this.turnstileLoaded = false;
            this.cdr.detectChanges();
          }
        });
        console.log('✅ Turnstile renderizado exitosamente en:', containerId);
      } catch (error) {
        console.error('Error al renderizar Turnstile:', error);
        this.errorMessage = 'Error al cargar el sistema de verificación';
      }
    }, 500); // Aumentado a 500ms para asegurar que el DOM esté listo
  }

  private resetTurnstile(): void {
    if (!this.isBrowser || !window.turnstile || !this.turnstileWidgetId) return;

    try {
      window.turnstile.reset(this.turnstileWidgetId);
      this.turnstileToken = null;
      this.turnstileLoaded = false;
      this.renderTurnstileAttempts = 0; // Resetear contador de intentos
    } catch (error) {
      console.error('Error al resetear Turnstile:', error);
    }
  }

  // Paso 1: Verificar email
  onCheckEmail(): void {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    if (!this.turnstileToken) {
      this.errorMessage = 'Por favor, complete la verificación de seguridad';
      return;
    }

    this.emailChecking = true;
    this.errorMessage = '';

    const email = this.emailForm.value.email;
    
    this.authService.checkEmail({ 
      email, 
      turnstileToken: this.turnstileToken 
    }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          if (response.data.exists) {
            this.errorMessage = 'Este email ya está registrado. Por favor, inicia sesión.';
            this.emailAvailable = false;
          } else {
            this.emailAvailable = true;
            this.currentStep = 2;
            // Forzar detección de cambios para actualizar el DOM
            this.cdr.detectChanges();
            // Resetear Turnstile para el siguiente paso
            this.resetTurnstile();
            // Re-renderizar Turnstile después de que el DOM se actualice
            setTimeout(() => {
              this.renderTurnstile();
            }, 600);
          }
        }
        this.emailChecking = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error al verificar email:', error);
        this.errorMessage = error.error?.message || 'Error al verificar el email. Por favor, intente nuevamente.';
        this.emailChecking = false;
        this.resetTurnstile();
        this.cdr.detectChanges();
      }
    });
  }

  // Paso 2: Registrar usuario
  onSubmitRegister(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    if (!this.turnstileToken) {
      this.errorMessage = 'Por favor, complete la verificación de seguridad';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const email = this.emailForm.value.email;
    const formData = this.registerForm.value;

    // Convertir firstname y lastname a mayúsculas
    const firstname = formData.firstname.toUpperCase();
    const lastname = formData.lastname.toUpperCase();

    // Obtener teléfono completo con código de país
    const fullPhone = this.getFullPhoneNumber();

    this.authService.register({
      profile: {
        firstname,
        lastname,
        email,
        phone: fullPhone, // Enviar número completo con código de país
        password: formData.password
      },
      turnstileToken: this.turnstileToken
    }).subscribe({
      next: (response) => {
        if (response.success) {
          // Ahora el usuario debe activar su cuenta por email
          console.log('✅ Registro exitoso, mostrando mensaje de activación...');
          this.registeredEmail = email;
          this.registrationMessage = response.data?.message || response.message || 'Usuario registrado exitosamente. Por favor revisa tu email para activar tu cuenta.';
          this.registrationSuccess = true;
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('Error en registro:', error);
        this.errorMessage = error.error?.message || 'Error al registrar. Por favor, intente nuevamente.';
        this.isLoading = false;
        this.resetTurnstile();
      }
    });
  }

  // Volver al paso anterior
  goBackToEmailStep(): void {
    this.currentStep = 1;
    this.emailAvailable = false;
    this.registerForm.reset();
    this.errorMessage = '';
    
    // Forzar detección de cambios para actualizar el DOM
    this.cdr.detectChanges();
    
    // Resetear y re-renderizar Turnstile
    this.resetTurnstile();
    setTimeout(() => {
      this.renderTurnstile();
    }, 600);
  }

  // Helpers para validación de formularios
  isFieldInvalid(formGroup: FormGroup, fieldName: string): boolean {
    const field = formGroup.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(formGroup: FormGroup, fieldName: string): string {
    const field = formGroup.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'Este campo es requerido';
    if (field.errors['email']) return 'Email inválido';
    if (field.errors['minlength']) return `Mínimo ${field.errors['minlength'].requiredLength} caracteres`;
    if (field.errors['pattern']) return 'Formato inválido';
    if (field.errors['invalidPhone']) return getPhoneValidationMessage(this.selectedCountry);
    if (field.errors['mediumPassword']) return 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número';
    
    return '';
  }

  getPasswordMatchError(): string {
    const form = this.registerForm;
    if (form.errors?.['passwordMismatch'] && form.get('confirmPassword')?.touched) {
      return 'Las contraseñas no coinciden';
    }
    return '';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Country selector methods
  getSelectedCountry(): Country | undefined {
    return this.countries.find(c => c.code === this.selectedCountry);
  }

  selectCountry(country: Country): void {
    this.selectedCountry = country.code;
    this.showCountryDropdown = false;
    // Re-validar el teléfono con el nuevo país
    const phoneControl = this.registerForm.get('phone');
    if (phoneControl) {
      phoneControl.updateValueAndValidity();
    }
  }

  toggleCountryDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showCountryDropdown = !this.showCountryDropdown;
  }

  getPhonePlaceholder(): string {
    const country = this.getSelectedCountry();
    return country?.placeholder || 'Ingrese su teléfono';
  }

  getPhoneMaxLength(): number {
    const country = this.getSelectedCountry();
    return country?.maxDigits || 15;
  }

  onPhoneInput(): void {
    // Eliminar caracteres no numéricos
    const phoneControl = this.registerForm.get('phone');
    if (!phoneControl) return;

    let phone = phoneControl.value.replace(/\D/g, '');
    const maxLength = this.getPhoneMaxLength();

    // Limitar al máximo de dígitos permitidos
    if (phone.length > maxLength) {
      phone = phone.substring(0, maxLength);
    }

    phoneControl.setValue(phone, { emitEvent: false });
  }

  validatePhoneField(): string | null {
    const phoneControl = this.registerForm.get('phone');
    if (!phoneControl || !phoneControl.value) return null;

    const cleanPhone = phoneControl.value.replace(/\D/g, '');
    if (!cleanPhone) return null;

    if (!validatePhoneNumber(cleanPhone, this.selectedCountry)) {
      return getPhoneValidationMessage(this.selectedCountry);
    }

    return null;
  }

  // Método para obtener el teléfono completo con código de país
  getFullPhoneNumber(): string {
    const phoneControl = this.registerForm.get('phone');
    if (!phoneControl) return '';
    return formatPhoneNumber(phoneControl.value, this.selectedCountry);
  }

  // Navegar a login después del registro exitoso
  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
